/**
 * Meetings.
 *
 * The list is a date range, because that is the question a calendar asks. The range is
 * sent as two instants with the viewer's own offset on them, and everything comes back
 * as instants too. No part of this has to know which timezone anybody is in, which is
 * the whole point of storing a moment rather than a wall clock.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import type { Meeting, MeetingCreate, MeetingStatus, MeetingUpdate } from '@/api/schema';

export const MEETINGS_KEY = ['meetings'] as const;

export function useMeetings(start: Date, end: Date) {
  return useQuery({
    queryKey: [...MEETINGS_KEY, 'range', start.toISOString(), end.toISOString()],
    queryFn: () =>
      api.get<Meeting[]>('/api/v1/meetings', {
        query: { from: start.toISOString(), to: end.toISOString() },
      }),
    // Kept while the next week loads, so navigating does not blank the grid.
    placeholderData: (previous) => previous,
  });
}

export function useCreateMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MeetingCreate) => api.post<Meeting>('/api/v1/meetings', body),
    onSuccess: async () => {
      await Promise.all(
        [MEETINGS_KEY, ['dashboard'], ['notifications']].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      );
    },
  });
}

export function useUpdateMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: MeetingUpdate }) =>
      api.patch<Meeting>(`/api/v1/meetings/${id}`, changes),
    onSuccess: async () => {
      await Promise.all(
        [MEETINGS_KEY, ['dashboard'], ['notifications']].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      );
    },
  });
}

export function useDeleteMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    // The endpoint answers 204 with no body, so there is nothing to type as a
    // result: `never` says that, where `void` is a return type rather than a value.
    mutationFn: (id: string) => api.delete<never>(`/api/v1/meetings/${id}`),
    onSuccess: async () => {
      await Promise.all(
        [MEETINGS_KEY, ['dashboard'], ['notifications']].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      );
    },
  });
}

/**
 * Meetings that have been and gone with nothing recorded.
 *
 * Its own endpoint rather than a filter over a range, because "needs a log" is not
 * bounded by a week; the one from three months ago that nobody wrote up is exactly the
 * one worth surfacing.
 */
export function useMeetingsNeedingALog() {
  return useQuery({
    queryKey: [...MEETINGS_KEY, 'needs-log'],
    queryFn: () => api.get<Meeting[]>('/api/v1/meetings/needs-log'),
  });
}

export function useLogMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      outcome,
      notes,
    }: {
      id: string;
      outcome: MeetingStatus;
      notes?: string | undefined;
    }) => api.post<Meeting>(`/api/v1/meetings/${id}/log`, { outcome, notes: notes ?? null }),
    onSuccess: async () => {
      await Promise.all(
        [MEETINGS_KEY, ['dashboard'], ['notifications']].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      );
    },
  });
}

/** All meetings on the candidate record, including older meetings outside the calendar range. */
export function useCandidateMeetings(candidateId: string) {
  return useQuery({
    queryKey: [...MEETINGS_KEY, 'candidate', candidateId],
    queryFn: () => api.get<Meeting[]>(`/api/v1/meetings/candidate/${candidateId}`),
  });
}
